// Login form. Email + password → POST /auth/login → store the pair,
// then navigate to the `from` query param if present, else /.

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, auth } from "../lib/api";
import { useAuthStore } from "../lib/store";
import { pushToast } from "../components/Toast";

export function LoginPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const from = searchParams.get("from") || "/";

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const pair = await auth.login(email, password);
      setSession(pair.access_token, pair.refresh_token, email);
      pushToast("success", "Signed in");
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Authentication failed");
      } else {
        setError("Authentication failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="card w-full max-w-md p-8">
        <div className="mb-6 flex items-center gap-2">
          <span aria-hidden className="inline-block h-8 w-8 rounded bg-brand-600" />
          <h1 className="text-xl font-semibold text-slate-900">CaseLens</h1>
        </div>
        <h2 className="mb-4 text-lg font-medium text-slate-800">Sign in to your tenant</h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && (
            <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-6 text-xs text-slate-500">
          Founding cohort only. New tenants are onboarded manually — contact your CaseLens
          administrator.
        </p>
        <p className="mt-2 text-xs text-slate-400">
          Trouble signing in? <Link to="/login" className="text-brand-700 underline">
            Reset form
          </Link>
        </p>
      </div>
    </div>
  );
}
